import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Mail } from 'lucide-react';

const LinkedInIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect width="4" height="12" x="2" y="9" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const team = [
  {
    name: 'Dr. Mahmoud Gad, PhD',
    role: 'Principal Investigator',
    focus: 'Leads product vision and partnership strategy with a focus on actionable AI.',
    email: 'm.gad1@lancaster.ac.uk',
    linkedIn: 'https://www.linkedin.com/in/mahmoud-gad-30078b336/',
  },
  {
    name: 'Professor Steven Young, PhD',
    role: 'Lead Researcher',
    focus: 'Shapes the assessment framework, grounding the platform in rigorous disclosure insight.',
  },
  {
    name: 'Ewan Warburton',
    role: 'Software Engineer',
    focus: 'Architects the end-to-end experience, ensuring performance and reliability.',
    linkedIn: 'https://www.linkedin.com/in/ewanwarburton',
  },
  {
    name: 'Ranbir Kochar',
    role: 'Data Scientist',
    focus: 'Builds the analytical models and tuning pipelines that extract precise evidence.',
  },
];

export function Team() {
  const reduceMotion = useReducedMotion();
  return (
    <section className="w-full bg-[var(--surface-ambient)]/70 py-24">
      <div className="mx-auto flex max-w-6xl flex-col gap-16 px-6">
        <div className="max-w-2xl space-y-4">
          <p className="eyebrow">
            The team
          </p>
          <h2 className="font-display text-3xl font-semibold !tracking-display text-[var(--text-primary)] md:text-4xl">
            Multi-disciplinary leaders keeping modern slavery reporting accountable.
          </h2>
          <p className="text-lg leading-relaxed text-[var(--text-secondary)]">
            We blend AI engineering, corporate reporting research, and real-world compliance operations so that every
            release is grounded in what compliance teams need.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {team.map((member, index) => (
            <motion.article
              key={member.name}
              className="flex h-full flex-col justify-between rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/85 p-8 shadow-[0_24px_48px_rgba(15,23,42,0.08)]"
              initial={reduceMotion ? false : { opacity: 0, y: 24 }}
              whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.05, ease: 'easeOut' }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <div className="space-y-4">
                <p className="eyebrow-muted">
                  {member.role}
                </p>
                <h3 className="text-xl font-semibold text-[var(--text-primary)]">{member.name}</h3>
                <p className="text-sm leading-relaxed text-[var(--text-secondary)]">{member.focus}</p>
              </div>
              <div className="mt-6 flex items-center gap-3">
                {member.email && (
                  <a
                    aria-label={`Email ${member.name}`}
                    href={`mailto:${member.email}`}
                    className="flex h-10 w-10 items-center justify-center rounded-2xl border border-[rgba(31,76,84,0.16)] text-primary-500 transition-colors hover:bg-[rgba(31,76,84,0.08)]"
                  >
                    <Mail className="h-4 w-4" />
                  </a>
                )}
                {member.linkedIn && (
                  <a
                    aria-label={`LinkedIn profile for ${member.name}`}
                    href={member.linkedIn}
                    target="_blank"
                    rel="noreferrer"
                    className="flex h-10 w-10 items-center justify-center rounded-2xl border border-[rgba(31,76,84,0.16)] text-primary-500 transition-colors hover:bg-[rgba(31,76,84,0.08)]"
                  >
                    <LinkedInIcon />
                  </a>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
